import { Button, Checkbox, Flex, HStack, Text, VStack } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { getSdk } from "~/__generated__/graphql";
import client from "~/config/client";
import useNotify from "~/hooks/use-notify";
import InputField from "../forms/input-field";
import { LargeBreak, MediumBreak } from "../landing/v2/helpers";
const NotifyForm = () => {
  const schema = z.object({
    firstName: z.string({ coerce: true, required_error: "Please enter first name" }),

    lastName: z.string({ coerce: true, required_error: "Please enter first name" }),

    email: z.string({ coerce: true }).email("Invalid email address"),
    company: z.string({ coerce: true }),
    jobTitle: z.string({ coerce: true }),
    onAccept: z.boolean().refine((value) => value === true, {
      message: "You must agree to the terms and conditions",
    }),
  });

  type Schema = z.infer<typeof schema>;
  const { formState, register, handleSubmit, reset } = useForm<Schema>({
    resolver: zodResolver(schema),
  });
  const notify = useNotify();
  const subscribe = handleSubmit(async (data) => {
    try {
      const sdk = getSdk(client);

      const { createSubscriber } = await sdk.CreateSubscriber({
        data: {
          email: data.email,
          name: `${data.firstName ?? ""} ${data.lastName ?? ""}`,
          jobTitle: data.jobTitle,
          company: data.company,
        },
      });

      const advantePdfGuideSent = createSubscriber !== null;

      reset();
      notify(<>{advantePdfGuideSent && <>Copy will be sent to your email&nbsp;shortly.</>}</>, {
        title: advantePdfGuideSent ? <>Thank you!</> : null,
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      if (e.response.errors[0].extensions.error.name === "ValidationError") {
        notify(<>Email is already subscribed.</>, {
          variant: "success",
        });
      } else
        notify(<>There&rsquo;s an error in submitting your email address.</>, {
          variant: "danger",
        });
    }
  });

  return (
    <Flex
      py={{ xl: "100px", lg: "80px", base: "64px" }}
      px={{ xl: "160px", lg: "24px", base: "16px" }}
      fontFamily={"'Inter', sans-serif"}
      mx="auto"
      direction={"column"}
      bgColor="white"
    >
      <Flex
        direction={"column"}
        align="center"
        mb={{ lg: "64px", base: "40px" }}
        textAlign={"center"}
      >
        <Text
          fontSize={{ lg: "18px", base: "14px" }}
          color="rgba(13, 48, 80, 0.8)"
          lineHeight={{ lg: "28px", base: "14px" }}
          letterSpacing={"0.05em"}
          mb={{ lg: "16px", base: "12px" }}
        >
          EVENTS
        </Text>
        <Text
          fontSize={{ lg: "56px", base: "32px" }}
          color="#0D3050"
          lineHeight={{ lg: "64px", base: "36px" }}
          fontFamily={"Georgia"}
          mb={{ lg: "24px", base: "16px" }}
          letterSpacing={{ base: "-0.02em" }}
        >
          Get Notified of the Latest <LargeBreak /> <MediumBreak /> Events and Resources
        </Text>
        <Text
          fontSize={{ lg: "20px", base: "16px" }}
          color="rgba(13, 48, 80, 0.8)"
          lineHeight={{ lg: "32px", base: "24px" }}
          letterSpacing={{ base: "0.02em" }}
          textAlign={"center"}
          px={{ base: "18.5px" }}
          maxWidth={"1007px"}
        >
          Stay up-to-date on the latest career events and resources by signing up for our{" "}
          <MediumBreak /> email updates. By filling out the form, you&apos;ll receive regular
          updates about our <MediumBreak />
          upcoming events, webinars, and other career resources. Don&apos;t miss out on the{" "}
          <MediumBreak /> chance to take your career to the next level.
        </Text>
      </Flex>
      <Flex
        direction="column"
        as="form"
        onSubmitCapture={subscribe}
        w={{ lg: "556px" }}
        mx="auto"
        gap={"24px"}
      >
        <Flex direction={{ lg: "row", base: "column" }} gap={{ lg: "32px", base: "24px" }} w="full">
          <InputField
            chakraInputProps={{
              placeholder: "First name",
              alignItems: "center",
              backgroundColor: "white",
              height: "40px",
            }}
            id={"firstName"}
            {...register("firstName")}
            errorMsg={formState.errors.firstName?.message}
            label="First name"
          />
          <InputField
            chakraInputProps={{
              placeholder: "Last name",
              alignItems: "center",
              backgroundColor: "white",
              height: "40px",
            }}
            id={"lastName"}
            {...register("lastName")}
            errorMsg={formState.errors.lastName?.message}
            label="Last name"
          />
        </Flex>
        <InputField
          chakraInputProps={{
            placeholder: "you@company.com",
            alignItems: "center",
            backgroundColor: "white",
            height: "40px",
          }}
          id={"workEmail"}
          {...register("email")}
          errorMsg={formState.errors.email?.message}
          label="Work Email"
        />
        <InputField
          chakraInputProps={{
            placeholder: "Organization name",
            alignItems: "center",
            backgroundColor: "white",
            height: "40px",
          }}
          id={"orgCompany"}
          {...register("company")}
          errorMsg={formState.errors.company?.message}
          label="Organization/Company"
        />
        <InputField
          chakraInputProps={{
            placeholder: "Job title",
            alignItems: "center",
            backgroundColor: "white",
            height: "40px",
          }}
          id={"jobTitle"}
          {...register("jobTitle")}
          errorMsg={formState.errors.jobTitle?.message}
          label="Job Title"
        />
        <HStack align={"start"} spacing={"12px"}>
          <Checkbox
            {...register("onAccept")}
            iconSize={"sm"}
            size={"lg"}
            styleConfig={{
              baseStyle: {
                control: {
                  color: "#F9D209",
                  width: "20px",
                  height: "20px",
                  rounded: "base",

                  _checked: { border: "1px solid #F9D209" },
                  _empty: {
                    border: formState.errors.onAccept?.message
                      ? "1px solid red"
                      : "1px solid #D0D5DD",
                  },
                },
              },
            }}
          />
          <VStack color="rgba(13, 48, 80, 0.8)">
            <Text mb="20px" fontSize={"16px"} lineHeight={"24px"}>
              By checking this box, you consent to receive promotional emails from Advante. These
              emails may include information about upcoming events, special offers, or other updates
              related to our coaching programs.
            </Text>
            <Text fontStyle={"italic"} fontSize={"14px"} lineHeight={"24px"}>
              You can unsubscribe at any time by clicking the &apos;unsubscribe&apos; link in the
              footer of our emails. Please review our Privacy Policy for more information on how we
              use and protect your personal data.
            </Text>
          </VStack>
        </HStack>
        <Flex justify={"end"} w="full">
          <Button type="submit" height={"48px"} w={{ lg: "108px", base: "full" }}>
            Submit
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default NotifyForm;
